import React from "react";
import { ShieldCheck, BookOpenCheck, ExternalLink } from "lucide-react";
import { useSeo } from "@/hooks/useSeo";

export const VERIFY_URL = "https://gcsb.uk/verify";
export const REGISTER_URL = "https://gcsb.uk/register";

const LINKS = [
  {
    href: VERIFY_URL, icon: ShieldCheck, testid: "verify-gcsb-portal",
    eyebrow: "Verification Authority", title: "Credential Verification Portal",
    text: "Confirm a CSI/GCSB certificate, credential number, digital badge, qualification, membership grade, award date or fellowship status. Search by verification ID or holder name.",
    cta: "Verify a credential",
  },
  {
    href: REGISTER_URL, icon: BookOpenCheck, testid: "verify-gcsb-register",
    eyebrow: "Public Register", title: "Official Register of Credential Holders",
    text: "Browse the public record of CSCE, CSP, CSL and CCSO holders, PGDCSL graduates and Fellows - with credential, grade, country, award year, CPD status and verification ID.",
    cta: "Open the public register",
  },
];

export const VerificationLinks = ({ compact = false }) => (
  <div className={`grid gap-4 ${compact ? "md:grid-cols-2" : "md:grid-cols-2"}`}>
    {LINKS.map(({ href, icon: Icon, testid, eyebrow, title, text, cta }) => (
      <a key={href} href={href} target="_blank" rel="noopener noreferrer" data-testid={testid}
         className="group card-warm p-6 flex flex-col hover:border-[var(--ink)] transition-colors">
        <div className="w-11 h-11 rounded-xl bg-[var(--navy-deep)] text-[var(--gold-soft)] grid place-items-center"><Icon size={20}/></div>
        <div className="eyebrow mt-4">{eyebrow}</div>
        <h3 className="font-display text-2xl leading-tight mt-1">{title}</h3>
        {!compact && <p className="text-sm text-zinc-700 mt-2 flex-1">{text}</p>}
        <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--terracotta)] group-hover:underline">
          {cta} <ExternalLink size={14}/>
        </span>
      </a>
    ))}
  </div>
);

export default function VerifyPage() {
  useSeo({
    title: "Verify a CSI credential",
    description: "Verify CSI certificates and credentials through the GCSB Credential Verification Portal and Public Register.",
    path: "/verify",
  });
  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="verify-page">
      <div className="eyebrow mb-2">Verification</div>
      <h1 className="font-display text-4xl sm:text-5xl leading-tight">Verify a CSI credential</h1>
      <p className="text-sm sm:text-base text-zinc-700 mt-4 max-w-3xl">
        CSI certificates are recorded with the Global Converged Security Board (GCSB), the independent verification authority for
        CSCE, CSP, CSL, CCSO and PGDCSL credentials. Employers and third parties can confirm any credential through GCSB's public
        records - both open in a new tab.
      </p>
      <div className="mt-8"><VerificationLinks/></div>
      <div className="mt-8 rounded-2xl border border-[var(--border-warm)] bg-[var(--muted-surface)] p-5 text-sm text-zinc-700" data-testid="verify-help">
        <b>How to verify:</b> enter the verification ID or certificate number printed on the certificate in the GCSB portal, optionally with
        the holder's surname. For help, contact <a href="mailto:info@convergedsecurity.es" className="underline">info@convergedsecurity.es</a>.
      </div>
    </div>
  );
}
